import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';
import { ScheduleOutlined, BusinessOutlined } from '@mui/icons-material';
import KarenHeadshot from '../assets/Karen_Brown_Headshot.jpg';

const HeroSection = () => {
  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundImage: 'url(/images/hero.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        px: 3,
        pb: { xs: 5, md: 0 },
        pt: { xs: 5, sm: 12, md: 0 },
        color: 'black',
      }}
    >
      {/* Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(102, 107, 105, 0.85), rgba(240, 255, 250, 0.85), rgba(102, 107, 105, 0.85))',
          backgroundSize: '200% 200%',
          animation: 'gradientPushRightLoop 15s ease-in-out infinite',
          backdropFilter: 'blur(6px)',
          zIndex: 1,
        }}
      />

      <Container maxWidth="lg" sx={{
          position: 'relative',
          zIndex: 2,
          height: { md: '100%' },
          display: 'flex',
          alignItems: { md: 'center' },
          transform: { md: 'translateY(-10%)' }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            gap: { xs: 4, sm: 6, md: 8 },
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          {/* Headshot */}
          <Box
            component="img"
            src={KarenHeadshot}
            alt="Karen Brown Headshot"
            sx={{
              width: { xs: '220px', sm: '260px', md: '340px' },
              height: 'auto',
              borderRadius: '20px',
              boxShadow: 4,
              flexShrink: 0,
              mx: { xs: 'auto', md: 0 },
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />

          {/* Hero Text */}
          <Box sx={{ flex: 1, width: '100%' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.4 }}
            >
              <Typography variant="h1" fontWeight="bold" gutterBottom>
                Karen Brown, CDC Certified Divorce Coach® & Advisor
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.6 }}
            >
              <Typography variant="h5" gutterBottom>
                Clarity. Structure. Steady Guidance Through Divorce.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography sx={{ mb: 2 }}>
                Divorce is not just a legal process. It's a life transition that brings emotional, financial, and logistical complexity,
                often requiring important decisions to be made quickly and under significant pressure. My role is to bring preparation and
                clarity to the process so clients can move through it thoughtfully and work effectively with their legal and professional team.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 2 }}
            >
              {/* Button Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  gap: 2,
                  mt: 2,
                  mb: { xs: 4, md: 0 },
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  component={Link}
                  to="contact"
                  smooth={true}
                  duration={600}
                  startIcon={<ScheduleOutlined />}
                >
                  Schedule an Introductory Call
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="for law firms"
                  smooth={true}
                  duration={600}
                  startIcon={<BusinessOutlined />}
                  sx={{
                    borderColor: 'rgba(0,0,0,0.55)',
                    color: 'rgba(0,0,0,0.75)',
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    '&:hover': {
                      borderColor: 'rgba(0,0,0,0.85)',
                      backgroundColor: 'rgba(255,255,255,0.4)',
                      color: 'rgba(0,0,0,0.9)',
                    },
                  }}
                >
                  For Law Firms
                </Button>
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const InfoSection = () => {
  return (
    <Box component="section" sx={{ py: { xs: 8, sm: 10, md: 15 }, px: 3, bgcolor: '#f9f9f9' }}>
      <Container maxWidth="md">
        <Box sx={{ mb: { xs: 8, md: 15 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            What is a Divorce Advisor?
          </Typography>
          <Typography sx={{ mb: 2 }}>
            When someone is going through a divorce, they need more than legal advice. They need clarity, a steady hand, and an experienced, trusted partner who understands the full picture.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            That's where a divorce advisor comes in.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            My role is to help stabilize the human side of the divorce process so clients can stay organized, make thoughtful decisions, and use their legal team effectively.
          </Typography>
          <Typography sx={{ mb: 2 }}>
          A divorce advisor empowers individuals to navigate one of life's most challenging transitions with strength, calm, and perspective. They don't make choices for clients, but rather equip them to make their own, with greater confidence and intention.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            A divorce advisor is a structured thinking partner who helps clients stay clear-headed and prepared during one of life's most destabilizing transitions.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Divorce affects every part of a person's life: identity, finances, parenting, and more. A divorce advisor helps clients stay centered and focused while managing big emotions, decisions, and complex next steps.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            And because clients are better prepared and more focused, they use their legal team more efficiently—saving both time and money throughout the process.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            A divorce advisor isn't there to push anyone toward divorce. They're there to help individuals move forward—whatever that path looks like—with confidence, self-respect, and a future they can feel good about.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Why Work With a Divorce Advisor?
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Divorce can make you feel like your life is spinning out of control. You're flooded with choices, court deadlines, parenting logistics, and questions that don't come with instructions.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Attorneys guide the legal strategy. Therapists support the emotional side of divorce. A divorce advisor works in the space between, helping bring structure and clarity to the many moving parts of the process.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            When clients are organized, clear-headed, and prepared, the entire divorce process tends to move more smoothly, and important decisions become easier to navigate. Here are some of the ways I support clients during the divorce process:
          </Typography>

          <Typography sx={{ mb: 2 }}><strong>Organizing the Process</strong><br />Sort through the noise. Clarify your priorities. Gather paperwork. Get ready for the meetings that matter.</Typography>

          <Typography sx={{ mb: 2 }}><strong>Staying Grounded in the Process</strong><br />High emotions lead to reactive decisions that can have lasting consequences. I help you pause, process, and act with intention, not fear.</Typography>

          <Typography sx={{ mb: 2 }}><strong>Support Without Judgment</strong><br />Friends and family mean well, but they often come with opinions. I offer neutral, confidential guidance so you can move forward on your terms.</Typography>

          <Typography sx={{ mb: 2 }}><strong>Preparing for Important Conversations</strong><br />Don't get lost in legal jargon or emotional chaos. I help you communicate effectively and show up strong.</Typography>

          <Typography sx={{ mb: 2 }}><strong>Reducing Cost Through Preparation</strong><br />When you're prepared and focused, your legal team can stay strategic by using their time where it has the greatest impact.</Typography>

          <Typography sx={{ mb: 2 }}><strong>Planning for What Comes Next</strong><br />I'm not here to rehash the past. I'm here to help you build a life that reflects your values, your priorities, and your power.</Typography>
        </Box>
      </Container>
    </Box>
  );
};

const HeroAndInfoSection = () => {
  return (
    <Box>
      <HeroSection />
      <InfoSection />
    </Box>
  );
};

export default HeroAndInfoSection;